import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Settings, FlaskConical, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useDemoMode } from '@/src/contexts/DemoModeContext'
import {
  getDemoModeSetting,
  setDemoModeSetting,
  type DemoModeSetting,
} from '@/src/lib/api/system-settings'

export default function AdminSystemSettings() {
  const { refreshDemoMode } = useDemoMode()
  const [setting, setSetting] = useState<DemoModeSetting | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    getDemoModeSetting()
      .then((data) => {
        if (!cancelled) setSetting(data)
      })
      .catch(() => {
        if (!cancelled) setError('ไม่สามารถโหลดการตั้งค่าโหมดเดโมได้')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleToggle = async (checked: boolean) => {
    setSaving(true)
    setError('')
    try {
      const updated = await setDemoModeSetting(checked)
      setSetting(updated)
      await refreshDemoMode()
      toast.success(updated.enabled ? 'เปิดโหมดเดโมแล้ว' : 'ปิดโหมดเดโมแล้ว')
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'ไม่สามารถบันทึกการตั้งค่าได้ กรุณาลองใหม่อีกครั้ง'
      setError(message)
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">ตั้งค่าระบบ</h1>
        <p className="text-muted-foreground">การตั้งค่าและการกำหนดค่าระบบ</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            โหมดเดโม (Demo Mode)
          </CardTitle>
          <CardDescription>
            สำหรับนำเสนอระบบกับลูกค้าบนเซิร์ฟเวอร์ UAT เท่านั้น
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              กำลังโหลด...
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                <div className="space-y-1">
                  <Label htmlFor="demo-mode-switch" className="text-base font-medium">
                    เปิดโหมดเดโม
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    ผู้ที่สมัครสมาชิกขณะเปิดโหมดนี้จะข้ามการยืนยัน OTP และการยืนยันตัวตน (KYC)
                    เข้าใช้งานได้ทันที และซื้อแพ็กเกจได้โดยไม่ตรวจสอบการชำระเงิน
                  </p>
                </div>
                <Switch
                  id="demo-mode-switch"
                  checked={setting?.enabled ?? false}
                  onCheckedChange={handleToggle}
                  disabled={saving || (!setting?.allowed && !setting?.enabled)}
                  aria-label="เปิดหรือปิดโหมดเดโม"
                />
              </div>

              {setting && !setting.allowed && (
                <Alert>
                  <AlertDescription>
                    เซิร์ฟเวอร์นี้ไม่อนุญาตให้เปิดโหมดเดโม (ไม่ได้ตั้งค่า <code>ALLOW_DEMO_MODE=true</code>)
                    โหมดเดโมใช้ได้เฉพาะบนเซิร์ฟเวอร์ UAT
                  </AlertDescription>
                </Alert>
              )}

              <Alert>
                <AlertDescription>
                  เมื่อปิดโหมดเดโม บัญชีที่สมัครไว้ระหว่างเปิดโหมดจะกลับไปต้องยืนยันตัวตน (KYC)
                  ตามปกติ บัญชีเหล่านี้ถูกติดป้ายเดโมไว้ในฐานข้อมูล (<code>customers.is_demo</code>)
                  เพื่อให้ล้างข้อมูลภายหลังได้
                </AlertDescription>
              </Alert>

              {error && (
                <p role="alert" className="text-sm text-red-600">
                  {error}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            การตั้งค่าอื่น ๆ
          </CardTitle>
          <CardDescription>ส่วนนี้อยู่ระหว่างการพัฒนา</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            ระบบการตั้งค่ากำลังอยู่ระหว่างการพัฒนา<br />
            จะสามารถตั้งค่าระบบ จัดการการแจ้งเตือน และกำหนดค่าต่างๆ ได้ในอนาคต
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
